import '../index.scss';

import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import axios from 'axios';
import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import CommonTable from '../../components/CommonTable';
import TopSection from '../../components/TopSection';
import { requestWithAuth } from '../../services/api';
import {
  aidOrganizationPlanList,
  industryList,
  staticFunctionFlags,
} from '../../utils/constants';
import { selectOptionFormatterFunctionFlag } from '../../utils/general';

const searchKeys = [
  'organization_name',
  'stytch_org_slug',
  'permanent_refcode',
];

interface RequestParams {
  page_size?: number;
  page?: number;
  q?: string;
  sort?: string;
  organization_name?: string;
  stytch_org_id?: string;
  permanent_refcode?: string;
}

interface FormVals {
  id?: number;
  stytch_org_id?: string;
  created?: string;
  modified?: string;
  stytch_org_slug?: string;
}

interface AidOrganizationUser {
  id?: number;
  aidorganization_id: number;
  aidorganization_member_id: number;
  is_admin: boolean;
  first_name: string;
  last_name: string;
  email: string;
}

// Port of the backend slug transform: keep alphanumerics and -._, replace
// everything else with a hyphen, and lowercase the result.
const slugify = (input: string) =>
  Array.from(input)
    .map((c) => (/[\p{L}\p{N}]/u.test(c) || '-._'.includes(c) ? c : '-'))
    .join('')
    .toLowerCase();

// Billing fields come from the org's latest org-level subscription
// (user == null). User subscriptions have their own Maxio customer.
const fetchOrgLevelBilling = (id, form) => {
  requestWithAuth(
    'vault-aid-organization-subscription',
    'GET',
    { aidorganization_id: id, sort: '-created', page_size: 200 },
    null,
  ).then((response) => {
    const orgSub = response?.results?.find((sub) => !sub.user);
    form.setFieldsValue({
      maxio_customer_id: orgSub?.maxio_customer_id ?? 'N/A',
      billing_portal_url: orgSub?.billing_portal_url || 'N/A',
    });
  });
};

const FormItems = ({
  form,
  formVals,
  isUpdate,
  isNexisAdmin,
  theme,
  functionFlags,
  accountReps,
}) => {
  // Tracks the last slug we auto-generated so we keep syncing it to the org
  // name until the user edits the slug themselves (or it was pre-filled on an
  // existing org).
  const autoSlugRef = useRef('');

  return (
    <>
      {isUpdate && (
        <>
          <Form.Item name="id" label="Aid Organization ID">
            <Input placeholder="Aid organization ID" disabled />
          </Form.Item>
          <Form.Item name="stytch_org_id" label="Stytch Organization ID">
            <Input placeholder="Stytch organization ID" disabled />
          </Form.Item>
          <Form.Item name="maxio_customer_id" label="Maxio Customer ID">
            <Input placeholder="Maxio customer ID" disabled />
          </Form.Item>
          <Form.Item name="billing_portal_url" label="Billing Portal URL">
            <Input placeholder="Billing portal URL" disabled />
          </Form.Item>
          <Form.Item name="created" label="Created">
            <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" disabled />
          </Form.Item>
          <Form.Item name="modified" label="Modified">
            <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" disabled />
          </Form.Item>
        </>
      )}
      <Form.Item
        name="organization_name"
        label="Organization Name"
        rules={[
          { required: true, message: 'Enter Organization Name' },
          {
            min: 2,
            message: 'Organization Name must be at least two characters',
          },
        ]}
      >
        <Input
          placeholder="Organization name"
          onChange={(e) => {
            const currentSlug = form.getFieldValue('stytch_org_slug');
            // Keep the slug in sync with the org name until the user takes
            // control of the slug field themselves.
            if (!currentSlug || currentSlug === autoSlugRef.current) {
              const newSlug = slugify(e.target.value);
              autoSlugRef.current = newSlug;
              form.setFieldValue('stytch_org_slug', newSlug);
            }
          }}
        />
      </Form.Item>
      <Form.Item
        name="stytch_org_slug"
        label="Organization Slug"
        rules={[
          { required: true, message: 'Enter Organization Slug' },
          {
            min: 2,
            message: 'Organization Slug must be at least two characters',
          },
        ]}
      >
        <Input placeholder="Organization slug" />
      </Form.Item>
      <Form.Item name="permanent_refcode" label="Permanent Ref Code">
        <Input
          placeholder="Permanent ref code"
          onChange={(e) =>
            form.setFieldValue(
              'permanent_refcode',
              e.target.value.toLowerCase().replace(/\s/g, ''),
            )
          }
        />
      </Form.Item>
      {!isNexisAdmin && (
        <Form.Item
          name="aidorg_function_flags"
          label={<div>AidOrg Function Flags</div>}
        >
          <Select
            mode="multiple"
            allowClear
            style={{ width: '100%' }}
            options={functionFlags.map(selectOptionFormatterFunctionFlag)}
          />
        </Form.Item>
      )}
      <Form.Item
        name="credits"
        label="Bulk Credits"
        initialValue={0}
        rules={[{ required: true, message: 'Enter Bulk Credits' }]}
        hidden={isNexisAdmin}
      >
        <InputNumber style={{ width: '100%' }} placeholder="Credits" />
      </Form.Item>
      {!isNexisAdmin && (
        <Form.Item name="theme" label="White Label">
          <Select
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="children"
          >
            {theme.map(({ theme__id, theme__name }) => (
              <Select.Option key={theme__id} value={theme__id}>
                {theme__name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}
      <Form.Item name="company" label="Company">
        <Input placeholder="Company" />
      </Form.Item>
      <Form.Item name="industry" label="Industry">
        <Select
          style={{ width: '100%' }}
          showSearch
          optionFilterProp="children"
        >
          {industryList.map(({ value }) => (
            <Select.Option key={value} value={value}>
              {value}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      {isUpdate && (
        <>
          <Form.Item label="Total Users">
            <Input value={formVals.total_user_count} disabled />
          </Form.Item>

          <Form.Item label="Active Users">
            <Input value={formVals.active_user_count} disabled />
          </Form.Item>

          <Form.Item label="Inactive Users">
            <Input value={formVals.inactive_user_count} disabled />
          </Form.Item>
        </>
      )}
      {!isNexisAdmin && (
        <Form.Item
          name="is_northwestern_mutual"
          label="Is Northwestern Mutual"
          valuePropName="checked"
        >
          <Checkbox />
        </Form.Item>
      )}
      {/**Do not show nexis client field for nexis users
       * but it should default to "yes" for all orgs they create.
       * Make visible for Aidentified admin users so that
       *  we can update old Nexis organizations.
       */}
      {!isNexisAdmin && (
        <Form.Item name="is_nexis" label="Is Nexis" valuePropName="checked">
          <Checkbox />
        </Form.Item>
      )}
      {!isNexisAdmin && (
        <>
          <Form.Item name="stytch_jit_domain" label="Stytch JIT Domain">
            <Input placeholder="Stytch JIT domain" />
          </Form.Item>
          <Form.Item name="sso_only" label="SSO Only" valuePropName="checked">
            <Checkbox />
          </Form.Item>
          <Form.Item name="sso_jit" label="SSO JIT" valuePropName="checked">
            <Checkbox />
          </Form.Item>
          <Form.Item name="maxio_core_id" label="Maxio Core ID">
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Maxio core ID"
            />
          </Form.Item>
        </>
      )}
      <Form.Item name="comment" label="Comment">
        <Input.TextArea />
      </Form.Item>
      {!isUpdate && (
        <>
          <Divider titlePlacement="left">Subscription</Divider>
          <Form.Item
            name="owner"
            label="Owner"
            rules={[{ required: true, message: 'Enter Owner' }]}
          >
            <Select
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) =>
                option?.children?.toString().includes(input)
              }
              filterSort={(optionA, optionB) =>
                (optionA?.children?.toString() || '')
                  .toLowerCase()
                  .localeCompare(
                    (optionB?.children?.toString() || '').toLowerCase(),
                  )
              }
            >
              {accountReps.map(({ value, text }) => (
                <Select.Option key={value} value={value}>
                  {text}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="plan"
            label="Plan"
            rules={[{ required: true, message: 'Select Plan' }]}
          >
            <Select
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
              placeholder="Plan"
              options={aidOrganizationPlanList}
              onChange={(value) => {
                if (value === 'premium_unlimited_nwm') {
                  form.setFieldValue('is_northwestern_mutual', true);
                }
              }}
            />
          </Form.Item>
          <Form.Item name="plan_credits" label="Recurring Credits">
            <InputNumber style={{ width: '100%' }} placeholder="Credits" />
          </Form.Item>
          <Form.Item name="max_seats" label="Max Seats">
            <InputNumber style={{ width: '100%' }} placeholder="Max Seats" />
          </Form.Item>
          <Form.Item name="expiry_date" label="Expiry Date">
            <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
          </Form.Item>
        </>
      )}
    </>
  );
};

const FormModal = ({
  form,
  isUpdate,
  isNexisAdmin,
  handleAidUpdate,
  handleAidCreate,
  isModalLoading,
  onClose,
  recordFormVals,
  error,
  theme,
  functionFlags,
  accountReps,
}) => {
  const [formVals, setFormVals] = useState<FormVals>({});
  const [userData, setUserData] = useState([]);
  const [usersPagination, setUsersPagination] = useState({
    total: 0,
    pageSize: 10,
    current: 1,
  });

  const fetchCurrentOrganizationUsers = (id, page, pageSize) => {
    const userInfo: AidOrganizationUser[] = [];
    const newParams = {
      page_size: pageSize,
      page,
      aidorganization_id: id,
      sort: 'created',
    };

    requestWithAuth(
      'vault-account-aid-organization',
      'GET',
      newParams,
      null,
    ).then((response) => {
      if (response && response.results) {
        response.results.map((item) => {
          userInfo.push({
            id: item.id,
            aidorganization_id: item.aidorganization_id,
            aidorganization_member_id: item.aidorganization_member_id,
            first_name: item.first_name,
            last_name: item.last_name,
            email: item.email,
            is_admin: item.is_admin,
          });
          return null;
        });
      }
      setUserData([...userInfo]);
      setUsersPagination((prevState) => ({
        ...prevState,
        total: response?.count ?? 0,
        current: page,
        pageSize,
      }));
    });
  };

  const handleUsersTableChange = (pagination) => {
    fetchCurrentOrganizationUsers(
      recordFormVals.id,
      pagination.current,
      pagination.pageSize,
    );
  };

  useEffect(() => {
    if (isUpdate && recordFormVals) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFormVals({ ...recordFormVals });

      form.setFieldsValue({
        id: recordFormVals.id || 'N/A',
        stytch_org_id: recordFormVals.stytch_org_id || 'N/A',
        created: recordFormVals.created ? dayjs(recordFormVals.created) : null,
        modified: recordFormVals.modified
          ? dayjs(recordFormVals.modified)
          : null,
        organization_name: recordFormVals.organization_name,
        stytch_org_slug: recordFormVals.stytch_org_slug,
        permanent_refcode: recordFormVals.permanent_refcode,
        aidorg_function_flags: recordFormVals.aidorg_function_flags || [],
        credits: recordFormVals.credits,
        company: recordFormVals.company ?? null,
        industry: recordFormVals.industry,
        comment: recordFormVals.comment,
        theme: recordFormVals.theme,
        is_northwestern_mutual: recordFormVals.is_northwestern_mutual ?? false,
        is_nexis: recordFormVals.is_nexis ?? false,
        stytch_jit_domain: recordFormVals.stytch_jit_domain ?? null,
        sso_only: recordFormVals.sso_only ?? false,
        sso_jit: recordFormVals.sso_jit ?? false,
        maxio_core_id: recordFormVals.maxio_core_id ?? null,
      });

      fetchCurrentOrganizationUsers(recordFormVals.id, 1, 10);
      fetchOrgLevelBilling(recordFormVals.id, form);
    }
  }, [recordFormVals, isUpdate, form]);

  const updateAdminField = (rowId: number, isAdmin: boolean) => {
    return requestWithAuth(
      `vault-account-aid-organization/${rowId}`,
      'PATCH',
      null,
      { is_admin: isAdmin },
    );
  };

  const onAdminToggle = async (checked: boolean, record) => {
    setUserData((prev) =>
      prev.map((item) =>
        item.id === record.id ? { ...item, is_admin: checked } : item,
      ),
    );
    const rollback = () => {
      setUserData((prev) =>
        prev.map((item) =>
          item.id === record.id ? { ...item, is_admin: !checked } : item,
        ),
      );
      message.error('Failed to update admin status');
    };
    try {
      const response = await updateAdminField(record.id, checked);
      // requestWithAuth resolves (never rejects): undefined on a swallowed
      // network error, { error } on a 400.
      if (!response || response.error) {
        rollback();
        return;
      }
      message.success('Admin status updated');
    } catch {
      rollback();
    }
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const payload = {
          organization_name: values.organization_name,
          stytch_org_slug: values.stytch_org_slug,
          permanent_refcode: values.permanent_refcode,
          credits: values.credits,
          company: values.company || null,
          industry: values.industry,
          comment: values.comment,
          aidorg_function_flags: values.aidorg_function_flags,
          is_northwestern_mutual: values.is_northwestern_mutual ?? false,
          // by default is_nexis is true for nexis admin, even if the checkbox is absent
          is_nexis: isNexisAdmin ? true : (values.is_nexis ?? false),
          stytch_jit_domain: values.stytch_jit_domain || null,
          sso_only: values.sso_only ?? false,
          sso_jit: values.sso_jit ?? false,
          maxio_core_id: values.maxio_core_id ?? null,
          ...(values.theme !== undefined && { theme: values.theme }),
        };
        if (isUpdate) {
          handleAidUpdate(formVals.id, payload);
        } else {
          const subPayload = {
            owner: values.owner,
            plan: values.plan,
            plan_credits: values.plan_credits,
            max_seats: values.max_seats,
            ...(values.expiry_date && {
              expiry_date: values.expiry_date.format('YYYY-MM-DD'),
            }),
            is_active: values.expiry_date
              ? !values.expiry_date.isBefore(dayjs(), 'day')
              : true,
          };
          handleAidCreate(payload, subPayload);
        }
      })
      .catch((errorInfo) => {
        if (errorInfo) {
          console.log(errorInfo);
          message.error('Please fill the mandatory fileds');
          return;
        }
      });
  };

  const newUserColumns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 150,
      render: (val, record) => (
        <Link
          to={`/user-editor/users?user_id=${record.aidorganization_member_id}`}
        >
          {val}
        </Link>
      ),
    },
    {
      title: 'First Name',
      dataIndex: 'first_name',
      key: 'first_name',
      width: 150,
    },
    {
      title: 'Last Name',
      dataIndex: 'last_name',
      key: 'last_name',
      width: 150,
    },
    {
      title: 'Is Admin',
      dataIndex: 'is_admin',
      key: 'is_admin',
      width: 120,
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) => onAdminToggle(e.target.checked, record)}
        />
      ),
    },
  ];

  return (
    <Modal
      title={isUpdate ? 'Update Aid Organization' : 'New Aid Organization'}
      open
      width={1000}
      okText={isUpdate ? 'Update' : 'Create'}
      onOk={handleSubmit}
      confirmLoading={isModalLoading}
      onCancel={onClose}
    >
      <Spin size="default" spinning={isModalLoading}>
        <Form labelCol={{ span: 7 }} wrapperCol={{ span: 13 }} form={form}>
          <FormItems
            form={form}
            formVals={formVals}
            isUpdate={isUpdate}
            isNexisAdmin={isNexisAdmin}
            theme={theme}
            functionFlags={functionFlags}
            accountReps={accountReps}
          />
          {userData.length > 0 && (
            <CommonTable
              columns={newUserColumns}
              data={userData}
              rowKey={(rec) => rec.email}
              size="small"
              pagination={{ ...usersPagination }}
              onTableChange={handleUsersTableChange}
            />
          )}
          {error && <div className="error-text">{error}</div>}
        </Form>
      </Spin>
    </Modal>
  );
};

const AidOrganization = ({ isNexisAdmin }) => {
  const paramsRef = useRef<RequestParams>({});
  const sourceRef = useRef<
    { cancel: (message?: string) => void; token: unknown } | undefined
  >(undefined);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [aidOrgananizationData, setAidOrganizationData] = useState([]);
  const [aidOrgananizationCount, setAidOrganizationCount] = useState([]);
  const [listPagination, setListPagination] = useState({
    total: 0,
    pageSize: 50,
    current: 1,
  });
  const { pageSize, current } = listPagination;
  const [sortInfo, setSortInfo] = useState({
    column: 'created',
    order: 'descend',
  });
  const { column, order } = sortInfo;
  const [isUpdate, setIsUpdate] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [recordFormVals, setRecordFormVals] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(
    () => searchKeys.map((key) => searchParams.get(key)).find(Boolean) ?? '',
  );

  const [theme, setTheme] = useState([]);
  const [accountReps, setAccountReps] = useState([]);
  const [functionFlags] = useState([...staticFunctionFlags]);
  const [searchType, setSearchType] = useState(
    () =>
      searchKeys.find((key) => searchParams.get(key)) ?? 'organization_name',
  );

  const [form] = Form.useForm();

  const fetchAidOrganization = useCallback(() => {
    const newParams = {
      ...Object.fromEntries(searchParams),
      ...paramsRef.current,
      page_size: pageSize,
      page: current,
    };
    if (searchQuery) {
      newParams[searchType] = searchQuery;
    }
    if (!newParams.sort) {
      newParams.sort = '-created';
    }

    if (sourceRef.current)
      sourceRef.current.cancel('Operation canceled by the user.');
    const { CancelToken } = axios; // eslint-disable-line import-x/no-named-as-default-member
    const source = CancelToken.source();
    sourceRef.current = source;
    // Block the table until the rows are current, so Edit can't open the
    // modal with a row from before the last save.
    setIsLoading(true);

    requestWithAuth(
      'vault-aid-organization',
      'GET',
      newParams,
      null,
      source.token,
    ).then((response) => {
      // A newer fetch replaced this one; let it manage the state.
      if (sourceRef.current !== source) return;
      if (response && response.results) {
        setAidOrganizationData(response.results);
        setAidOrganizationCount(response.count);
        setListPagination((prevState) => ({
          ...prevState,
          total: response.count,
        }));
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    });
  }, [pageSize, current, searchQuery, searchParams, searchType]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchAidOrganization();
  }, [fetchAidOrganization, column, order]);

  const fetchTheme = () => {
    const params = {
      key: 'theme',
    };
    requestWithAuth('admin-field-lookup', 'GET', params, null)
      .then((response) => {
        if (response && response.result && response.result.length) {
          setTheme(response.result);
        }
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  const fetchAccountReps = () => {
    requestWithAuth('admin-field-lookup', 'GET', { key: 'account_rep' }, null)
      .then((response) => {
        if (response && response.result && response.result.length) {
          const reps = response.result.map((rep) => ({
            value: rep.email,
            text: rep.email,
          }));
          reps.sort((a, b) => a.text.localeCompare(b.text));
          setAccountReps(reps);
        }
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  useEffect(() => {
    fetchTheme();
    fetchAccountReps();
    return () => {
      paramsRef.current = {};
    };
  }, []);

  const handleSearchQuery = (searchValue) => {
    setSearchQuery(searchValue || '');
    setListPagination((prevState) => ({
      ...prevState,
      current: 1,
    }));
  };

  const handleSearchTypeChange = (value: string) => {
    setSearchType(value);
    setSearchQuery('');

    setListPagination((prevState) => ({
      ...prevState,
      current: 1,
    }));

    paramsRef.current = {};

    setSearchParams({});
  };

  const handleTableChange = (pagination, filtersArg, sorter) => {
    setSelectedRowKeys([]);
    if (sorter && sorter.order && sorter.columnKey) {
      paramsRef.current.sort =
        sorter.order === 'descend' ? '-' + sorter.columnKey : sorter.columnKey;
      setSortInfo({
        column: sorter.columnKey,
        order: sorter.order,
      });
    } else {
      setSortInfo({
        column: '',
        order: '',
      });
      delete paramsRef.current.sort;
    }
    setListPagination({
      ...pagination,
      pageSize: pagination.pageSize,
      current: pagination.current,
    });
  };

  const handleFormReset = () => {
    paramsRef.current = {};
    setAidOrganizationData([]);
    setAidOrganizationCount([]);
    setIsLoading(true);
    setSortInfo({
      column: 'created',
      order: 'descend',
    });
    setListPagination({
      current: 1,
      pageSize: 50,
      total: 0,
    });
    setSearchQuery('');
    setSearchParams({});
    fetchAidOrganization();
  };

  const handleAidUpdate = (id, fields) => {
    setIsModalLoading(true);
    setError(null);

    requestWithAuth(`vault-aid-organization/${id}`, 'PATCH', null, fields)
      .then((response) => {
        if (response?.error) {
          setError(Object.values(response.error).join(', '));
          return;
        }

        message.success('Aid Organization updated successfully');
        setShowFormModal(false);
        fetchAidOrganization();
        setIsUpdate(false);
      })
      .catch(() => {
        message.error('Failed to update Aid Organization');
        setShowFormModal(false);
        setIsUpdate(false);
      })
      .finally(() => {
        setIsModalLoading(false);
      });
  };

  const handleAidCreate = (orgFields, subFields) => {
    setIsModalLoading(true);
    setError(null);

    requestWithAuth('vault-aid-organization', 'POST', null, orgFields)
      .then((response) => {
        if (response?.error) {
          setError(Object.values(response.error).join(', '));
          return;
        }
        // requestWithAuth resolves undefined on a swallowed network error
        // (and already showed a generic message); nothing was created.
        if (!response?.id) {
          return;
        }

        // Org created. Now create its subscription back-to-back.
        return requestWithAuth(
          'vault-aid-organization-subscription',
          'POST',
          null,
          {
            aidorganization_id: response.id,
            ...subFields,
          },
        ).then((subResponse) => {
          if (!subResponse || subResponse.error) {
            const detail = subResponse?.error
              ? Object.values(subResponse.error).join(', ')
              : 'please create it manually';
            setError(
              `Organization created, but subscription failed: ${detail}`,
            );
            fetchAidOrganization();
            return;
          }

          message.success(
            'Aid Organization and subscription created successfully',
          );
          setShowFormModal(false);
          fetchAidOrganization();
          setIsUpdate(false);
        });
      })
      .catch(() => {
        message.error('Failed to create Aid Organization');
        setShowFormModal(false);
        setIsUpdate(false);
      })
      .finally(() => {
        setIsModalLoading(false);
      });
  };

  const handleDelete = (id) => {
    setIsLoading(true);
    requestWithAuth(`vault-aid-organization/${id}`, 'DELETE', null, null).then(
      (response) => {
        if (response?.error) {
          message.error(Object.values(response.error).join(', '));
        } else if (response !== undefined) {
          // DELETE returns 204 No Content → response is '' (falsy but defined).
          // Only `undefined` signals a swallowed failure (already toasted).
          message.success('Aid Organization deleted');
          // The refetch clears the loading state when it completes.
          fetchAidOrganization();
          return;
        }
        setIsLoading(false);
      },
    );
  };

  const showUpdateForm = (record) => {
    setIsUpdate(true);
    setRecordFormVals({ ...record });
    setShowFormModal(true);
  };

  const columns = [
    {
      title: 'Org Name',
      dataIndex: 'organization_name',
      key: 'organization_name',
      width: 150,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortInfo.column === 'organization_name' && sortInfo.order,
      className: 'word-break',
    },
    {
      title: 'Org Slug',
      dataIndex: 'stytch_org_slug',
      key: 'stytch_org_slug',
      width: 200,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortInfo.column === 'stytch_org_slug' && sortInfo.order,
      className: 'word-break',
    },
    {
      title: 'Permanent Ref Code',
      dataIndex: 'permanent_refcode',
      key: 'permanent_refcode',
      width: 100,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortInfo.column === 'permanent_refcode' && sortInfo.order,
    },
    {
      title: 'Created',
      width: 120,
      dataIndex: 'created',
      key: 'created',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortInfo.column === 'created' && sortInfo.order,
      render: (val) => {
        return (
          <div
            style={{ width: '100%', wordBreak: 'break-word', display: 'block' }}
          >
            {val ? dayjs(val).format('MM/DD/YYYY') : ''}
          </div>
        );
      },
    },
    {
      title: 'Bulk Credits',
      width: 120,
      dataIndex: 'credits',
      key: 'credits',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortInfo.column === 'credits' && sortInfo.order,
    },
    {
      title: 'Total Users',
      dataIndex: 'total_user_count',
      key: 'total_user_count',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortInfo.column === 'total_user_count' && sortInfo.order,
      render: (val, record) => (
        <div className="column-style">
          <Link to={`/user-editor/users?aidorganization_id=${record.id}`}>
            {val || 0}
          </Link>
        </div>
      ),
    },
    {
      title: 'Active Users',
      dataIndex: 'active_user_count',
      key: 'active_user_count',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortInfo.column === 'active_user_count' && sortInfo.order,
      render: (val, record) => (
        <div className="column-style">
          <Link
            to={`/user-editor/users?aidorganization_id=${record.id}&user_type=admin,api,lite,internal,premium,setup`}
          >
            {val || 0}
          </Link>
        </div>
      ),
    },
    {
      title: 'Inactive Users',
      dataIndex: 'inactive_user_count',
      key: 'inactive_user_count',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortInfo.column === 'inactive_user_count' && sortInfo.order,
      render: (val, record) => (
        <div className="column-style">
          <Link
            to={`/user-editor/users?aidorganization_id=${record.id}&user_type=inactive`}
          >
            {val || 0}
          </Link>
        </div>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 120,
      onCell: () => ({
        onClick: (e) => {
          e.stopPropagation();
        },
      }),
      render: (text, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              onClick={() => showUpdateForm(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Delete?"
              onConfirm={() => handleDelete(record.id)}
            >
              <Button icon={<DeleteOutlined />} size="small" />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div>
        <TopSection
          placeholder="Search Aid Organization"
          count={aidOrgananizationCount}
          showSearch
          handleFormReset={handleFormReset}
          handleSearchQuery={handleSearchQuery}
          toggleCreateForm={() => {
            form.resetFields();
            setIsUpdate(false);
            setRecordFormVals(null);
            setShowFormModal(true);
          }}
          selectedRowKeys={selectedRowKeys}
          searchValue={searchQuery}
          searchPrefix={
            <Select
              value={searchType}
              onChange={handleSearchTypeChange}
              style={{ width: 180 }}
            >
              <Select.Option value="organization_name">Org Name</Select.Option>
              <Select.Option value="stytch_org_slug">Org Slug</Select.Option>
              <Select.Option value="permanent_refcode">Ref Code</Select.Option>
            </Select>
          }
        />
        <Spin size="default" spinning={isLoading}>
          <CommonTable
            columns={columns}
            data={aidOrgananizationData}
            onTableChange={handleTableChange}
            pagination={{ ...listPagination }}
            pageSize={listPagination.pageSize}
            loading={isLoading}
            rowKey={(rec) => rec.id}
            selectedRowKeys={selectedRowKeys}
          />
        </Spin>
      </div>
      {showFormModal && (
        <FormModal
          form={form}
          isUpdate={isUpdate}
          isNexisAdmin={isNexisAdmin}
          handleAidUpdate={handleAidUpdate}
          handleAidCreate={handleAidCreate}
          isModalLoading={isModalLoading}
          recordFormVals={recordFormVals}
          functionFlags={functionFlags}
          accountReps={accountReps}
          error={error}
          onClose={() => {
            form.resetFields();
            setIsUpdate(false);
            setShowFormModal(false);
            setRecordFormVals(null);
            setError(null);
          }}
          theme={theme}
        />
      )}
    </>
  );
};

export default AidOrganization;
